import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import {
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Maximize2,
  Minimize2,
  Loader2,
  Download,
  BookOpen,
  ExternalLink,
  RotateCcw,
} from 'lucide-react';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { logPdfError } from '../../services/api/uploads';

pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

type Stage = 'primary' | 'fallback' | 'failed';

interface Props {
  url: string;
  name?: string;
  allowDownload?: boolean;
  storageKey?: string;
  lessonId?: string;
}

const deviceKind = () =>
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent) ? 'mobile' : 'desktop';

const shellClass = 'rounded-2xl border border-white/10 overflow-hidden bg-[#090D1A]';
const toolBtn =
  'p-2 rounded-lg bg-white/5 text-white/70 hover:text-cyan-400 hover:bg-white/10 disabled:opacity-30 transition-colors';

/** Menangkap error render pembaca utama agar halaman materi tidak ikut mati */
class ReaderBoundary extends React.Component<
  { onError: (reason: string) => void; children: React.ReactNode },
  { crashed: boolean }
> {
  state = { crashed: false };
  static getDerivedStateFromError() {
    return { crashed: true };
  }
  componentDidCatch(error: Error) {
    this.props.onError(error?.message || 'render error');
  }
  render() {
    return this.state.crashed ? null : this.props.children;
  }
}

export function PdfReader({ url, name, allowDownload, storageKey, lessonId }: Props) {
  const [stage, setStage] = useState<Stage>('primary');
  const [attempt, setAttempt] = useState(0);

  const report = useCallback(
    (nextStage: Stage, reason: string) => {
      setStage(nextStage);
      logPdfError({ lesson_id: lessonId || '', stage: nextStage, reason, device: deviceKind() });
    },
    [lessonId],
  );

  const retry = () => {
    setStage('primary');
    setAttempt((a) => a + 1);
  };

  if (stage === 'failed') {
    return (
      <div data-testid="pdf-reader-failed" className={`${shellClass} px-6 py-14 text-center`}>
        <BookOpen className="w-10 h-10 mx-auto mb-4 text-cyan-400/70" strokeWidth={1.5} />
        <p className="text-white font-medium">Materi belum bisa ditampilkan di sini</p>
        <p className="text-white/50 text-sm mt-1.5 max-w-md mx-auto leading-relaxed">
          Perangkat Anda belum bisa menampilkan materi di dalam halaman ini. Silakan buka di tab baru — isinya sama
          persis — atau coba muat ulang pembacanya.
        </p>
        <div className="flex items-center justify-center gap-2 mt-6 flex-wrap">
          <a
            data-testid="pdf-open-new-tab"
            href={allowDownload ? url : `${url}#toolbar=0`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 bg-cyan-400 text-[#050914] font-medium px-5 py-2.5 rounded-lg hover:bg-cyan-300 transition-colors text-sm"
          >
            <ExternalLink className="w-4 h-4" strokeWidth={2} /> Buka di Tab Baru
          </a>
          <button
            data-testid="pdf-retry"
            onClick={retry}
            className="inline-flex items-center gap-2 border border-white/15 text-white/80 px-5 py-2.5 rounded-lg hover:bg-white/5 transition-colors text-sm"
          >
            <RotateCcw className="w-4 h-4" strokeWidth={2} /> Coba Lagi
          </button>
        </div>
      </div>
    );
  }

  if (stage === 'fallback') {
    return (
      <div data-testid="pdf-reader-fallback" className={shellClass}>
        <div className="flex items-center gap-2 px-3 py-2.5 border-b border-white/10 bg-[#0B1020]">
          <p className="text-xs text-white/50">Pembaca sederhana · {name || 'Materi PDF'}</p>
          <div className="ml-auto flex items-center gap-1.5">
            {allowDownload && (
              <a data-testid="pdf-download" href={url} target="_blank" rel="noreferrer" download={name} className={toolBtn} title="Download PDF">
                <Download className="w-4 h-4" strokeWidth={2} />
              </a>
            )}
            <button data-testid="pdf-retry" onClick={retry} className={toolBtn} title="Coba pembaca lengkap lagi">
              <RotateCcw className="w-4 h-4" strokeWidth={2} />
            </button>
          </div>
        </div>
        <iframe
          key={`fb-${attempt}`}
          title={name || 'Materi PDF'}
          src={allowDownload ? url : `${url}#toolbar=0&navpanes=0`}
          className="w-full border-0 bg-[#060A15]"
          style={{ height: '75vh', minHeight: 440 }}
          onError={() => report('failed', 'iframe fallback error')}
        />
        <p className="text-[11px] text-white/35 px-3 py-2">
          Tidak muncul juga?{' '}
          <button data-testid="pdf-goto-failed" onClick={() => setStage('failed')} className="text-cyan-400/80 hover:text-cyan-400 underline">
            Buka di tab baru
          </button>
        </p>
      </div>
    );
  }

  return (
    <ReaderBoundary key={`pb-${attempt}`} onError={(reason) => report('fallback', `boundary: ${reason}`)}>
      <PrimaryReader
        url={url}
        name={name}
        allowDownload={allowDownload}
        storageKey={storageKey}
        onFail={(reason) => report('fallback', reason)}
        onSwitch={() => setStage('fallback')}
      />
    </ReaderBoundary>
  );
}

function PrimaryReader({
  url,
  name,
  allowDownload,
  storageKey,
  onFail,
  onSwitch,
}: {
  url: string;
  name?: string;
  allowDownload?: boolean;
  storageKey?: string;
  onFail: (reason: string) => void;
  onSwitch: () => void;
}) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(() => {
    const saved = storageKey ? Number(localStorage.getItem(storageKey)) : 0;
    return saved > 0 ? saved : 1;
  });
  const [scale, setScale] = useState(1);
  const [width, setWidth] = useState(0);
  const [fullscreen, setFullscreen] = useState(false);
  const [slow, setSlow] = useState(false);
  const loaded = numPages > 0;

  useEffect(() => {
    const measure = () => setWidth(wrapRef.current?.clientWidth || 0);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [fullscreen]);

  useEffect(() => {
    if (loaded) return;
    const t = setTimeout(() => setSlow(true), 6000);
    return () => clearTimeout(t);
  }, [loaded]);

  useEffect(() => {
    if (storageKey && loaded) localStorage.setItem(storageKey, String(page));
  }, [page, storageKey, loaded]);

  const onLoad = useCallback(({ numPages: n }: { numPages: number }) => {
    setNumPages(n);
    setSlow(false);
    setPage((p) => Math.min(Math.max(p, 1), n));
  }, []);

  const go = (delta: number) => setPage((p) => Math.min(Math.max(p + delta, 1), numPages || 1));
  const pageWidth = Math.max(280, Math.min((width || 640) - 24, 1100)) * scale;

  const toolbar = (
    <div className="flex items-center gap-2 flex-wrap px-3 py-2.5 border-b border-white/10 bg-[#0B1020]">
      <div className="flex items-center gap-1">
        <button data-testid="pdf-prev-page" onClick={() => go(-1)} disabled={page <= 1} className={toolBtn}>
          <ChevronLeft className="w-4 h-4" strokeWidth={2} />
        </button>
        <div className="flex items-center gap-1.5 text-xs text-white/60">
          <input
            data-testid="pdf-page-input"
            type="number"
            value={page}
            min={1}
            max={numPages || 1}
            onChange={(e) => {
              const v = Number(e.target.value);
              if (v >= 1 && v <= (numPages || 1)) setPage(v);
            }}
            className="w-14 bg-[#050914] border border-white/10 rounded-md px-2 py-1 text-center text-white outline-none focus:border-cyan-400"
          />
          <span>/ {numPages || '—'}</span>
        </div>
        <button data-testid="pdf-next-page" onClick={() => go(1)} disabled={!numPages || page >= numPages} className={toolBtn}>
          <ChevronRight className="w-4 h-4" strokeWidth={2} />
        </button>
      </div>

      <div className="flex items-center gap-1 ml-auto">
        <button data-testid="pdf-zoom-out" onClick={() => setScale((s) => Math.max(0.6, +(s - 0.2).toFixed(2)))} className={toolBtn}>
          <ZoomOut className="w-4 h-4" strokeWidth={2} />
        </button>
        <span className="text-xs text-white/50 w-10 text-center">{Math.round(scale * 100)}%</span>
        <button data-testid="pdf-zoom-in" onClick={() => setScale((s) => Math.min(2.4, +(s + 0.2).toFixed(2)))} className={toolBtn}>
          <ZoomIn className="w-4 h-4" strokeWidth={2} />
        </button>
        {allowDownload && (
          <a data-testid="pdf-download" href={url} target="_blank" rel="noreferrer" download={name || 'materi.pdf'} className={toolBtn} title="Download PDF">
            <Download className="w-4 h-4" strokeWidth={2} />
          </a>
        )}
        <button
          data-testid="pdf-fullscreen"
          onClick={() => setFullscreen((f) => !f)}
          className={toolBtn}
          title={fullscreen ? 'Keluar layar penuh' : 'Layar penuh'}
        >
          {fullscreen ? <Minimize2 className="w-4 h-4" strokeWidth={2} /> : <Maximize2 className="w-4 h-4" strokeWidth={2} />}
        </button>
      </div>
    </div>
  );

  const body = (
    <div
      ref={wrapRef}
      className={`overflow-auto bg-[#060A15] flex justify-center ${fullscreen ? 'h-[calc(100vh-56px)]' : 'max-h-[75vh]'}`}
      style={{ minHeight: 400 }}
      onContextMenu={allowDownload ? undefined : (e) => e.preventDefault()}
    >
      <Document
        file={url}
        onLoadSuccess={onLoad}
        onLoadError={(e: Error) => onFail(`load error: ${e?.message || 'unknown'}`)}
        onSourceError={(e: Error) => onFail(`source error: ${e?.message || 'unknown'}`)}
        loading={
          <div className="flex flex-col items-center justify-center py-24 gap-3" data-testid="pdf-loading">
            <Loader2 className="w-7 h-7 text-cyan-400 animate-spin" strokeWidth={1.5} />
            <p className="text-xs text-white/40">Memuat materi…</p>
            {slow && (
              <button
                data-testid="pdf-switch-fallback"
                onClick={onSwitch}
                className="text-xs text-cyan-400/80 hover:text-cyan-400 underline"
              >
                Terlalu lama? Pakai pembaca sederhana
              </button>
            )}
          </div>
        }
        className="py-4"
      >
        <Page pageNumber={page} width={pageWidth} renderAnnotationLayer renderTextLayer className="shadow-2xl mx-auto [&>canvas]:rounded-lg" />
      </Document>
    </div>
  );

  if (fullscreen) {
    return (
      <div data-testid="pdf-reader" className="fixed inset-0 z-[80] bg-[#050914]">
        {toolbar}
        {body}
      </div>
    );
  }

  return (
    <div data-testid="pdf-reader" className={shellClass}>
      {toolbar}
      {body}
    </div>
  );
}
